import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  FolderOpen, Plus, Search, ArrowLeft, Users, Phone, MapPin, Mail,
  FileText, Upload, Trash2, Download, ChevronRight, Image as ImageIcon,
  Video, File, Edit2, Save, X, Loader2, ClipboardList, Camera, Filter
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'preventivo', label: 'Preventivo', icon: ClipboardList, color: '#F59E0B' },
  { value: 'contratto', label: 'Contratto', icon: FileText, color: '#8B5CF6' },
  { value: 'foto', label: 'Foto', icon: Camera, color: '#3B82F6' },
  { value: 'altro', label: 'Altro', icon: File, color: '#71717A' },
];

// ---- Client Form ----
const ClientForm = ({ client, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: client?.name || '',
    phone: client?.phone || '',
    address: client?.address || '',
    email: client?.email || '',
    notes: client?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Nome obbligatorio'); return; }
    setSaving(true);
    try {
      if (client) {
        await axios.put(`${API_URL}/api/clients/${client.client_id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/clients`, form, { withCredentials: true });
      }
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-[#E4E4E7] shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold text-[#09090B]">
          {client ? 'Modifica Cliente' : 'Nuovo Cliente'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-sm font-semibold">Nome / Ragione Sociale *</Label>
          <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="es. Mario Rossi SRL" data-testid="client-name-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1"><Phone className="w-3 h-3" /> Telefono</Label>
            <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="333 1234567" data-testid="client-form-phone" />
          </div>
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
            <Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="email@esempio.it" data-testid="client-form-email" />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> Indirizzo</Label>
          <Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
            placeholder="Via, Città" data-testid="client-form-address" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Note</Label>
          <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Note aggiuntive..." rows={2} data-testid="client-form-notes" />
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-[#0055FF] hover:bg-[#0044CC] text-white flex-1"
            data-testid="save-client-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
          <Button variant="outline" onClick={onCancel} data-testid="cancel-client-btn">
            <X className="w-4 h-4 mr-1" /> Annulla
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Client Folder View ----
const ClientFolder = ({ clientId, onBack }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [docFilter, setDocFilter] = useState('');
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState('altro');
  const [uploadTitle, setUploadTitle] = useState('');

  const fetchFolder = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clients/${clientId}/folder`, { withCredentials: true });
      setFolder(res.data);
    } catch (e) {
      // silenced
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchFolder(); }, [fetchFolder]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        await axios.post(`${API_URL}/api/clients/${clientId}/documents`, {
          file_data: reader.result,
          filename: file.name,
          type: uploadType,
          title: uploadTitle || file.name,
        }, { withCredentials: true });
        setUploadTitle('');
        fetchFolder();
      };
      reader.readAsDataURL(file);
    } catch (e) {
      alert('Errore caricamento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API_URL}/api/clients/${clientId}/documents/${docId}`, { withCredentials: true });
      fetchFolder();
    } catch (e) { alert('Errore eliminazione'); }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}/api/clients/${clientId}/documents/${doc.doc_id}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore download'); }
  };

  const downloadWorkOrderPdf = async (orderId) => {
    try {
      const res = await axios.get(`${API_URL}/api/work-orders/${orderId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Buono_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore download PDF'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-[#0055FF]" />
    </div>
  );

  if (!folder) return (
    <div className="text-center py-12 text-[#71717A]">Cliente non trovato</div>
  );

  const { client, work_orders, documents, work_logs } = folder;
  const filteredDocs = docFilter ? documents.filter(d => d.type === docFilter) : documents;

  const tabs = [
    { id: 'documents', label: 'Documenti', count: documents.length, icon: FileText },
    { id: 'work_orders', label: 'Buoni', count: work_orders.length, icon: ClipboardList },
    { id: 'media', label: 'Media', count: work_logs.reduce((s, l) => s + (l.media?.length || 0), 0), icon: ImageIcon },
  ];

  return (
    <div className="space-y-4" data-testid="client-folder">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-clients-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-[#09090B] truncate">{client.name}</h2>
          <div className="flex flex-wrap gap-3 text-sm text-[#71717A]">
            {client.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</span>}
            {client.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {client.address}</span>}
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-client-btn">
            <Edit2 className="w-4 h-4" />
          </Button>
          {client.phone && (
            <Button size="sm" className="bg-[#25D366] hover:bg-[#1DA851] text-white"
              onClick={() => {
                const p = (client.phone || '').replace(/[^0-9+]/g, '');
                const full = p.startsWith('+') ? p : `+39${p}`;
                window.open(`https://wa.me/${full.replace('+', '')}`, '_blank');
              }} data-testid="whatsapp-client-btn">
              <FaWhatsapp className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <ClientForm client={client} onSave={() => { setEditing(false); fetchFolder(); }}
          onCancel={() => setEditing(false)} />
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F4F4F5] p-1 rounded-lg overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap
              ${activeTab === t.id ? 'bg-white text-[#09090B] shadow-sm' : 'text-[#71717A] hover:text-[#09090B]'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{t.label}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === t.id ? 'bg-[#0055FF] text-white' : 'bg-[#E4E4E7] text-[#71717A]'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-3">
          {/* Upload section */}
          <Card className="border-[#E4E4E7] border-dashed">
            <CardContent className="pt-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DOC_TYPES.map(dt => (
                  <button key={dt.value} onClick={() => setUploadType(dt.value)}
                    className={`p-2 rounded-lg border text-center text-xs font-medium transition-all
                      ${uploadType === dt.value ? 'border-[#0055FF] bg-blue-50 text-[#0055FF]' : 'border-[#E4E4E7] text-[#71717A] hover:border-[#A1A1AA]'}`}
                    data-testid={`doc-type-${dt.value}`}>
                    <dt.icon className="w-4 h-4 mx-auto mb-1" style={{ color: dt.color }} />
                    {dt.label}
                  </button>
                ))}
              </div>
              <Input placeholder="Titolo documento (opzionale)" value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)} data-testid="doc-title-input" />
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload}
                accept="image/*,video/*,.pdf,.doc,.docx" />
              <Button className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
                onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {uploading ? 'Caricamento...' : 'Carica Documento'}
              </Button>
            </CardContent>
          </Card>

          {/* Filter */}
          {documents.length > 0 && (
            <div className="flex gap-1 overflow-x-auto pb-1">
              <button onClick={() => setDocFilter('')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap
                  ${!docFilter ? 'bg-[#0055FF] text-white' : 'bg-[#F4F4F5] text-[#71717A] hover:bg-[#E4E4E7]'}`}>
                Tutti ({documents.length})
              </button>
              {DOC_TYPES.map(dt => {
                const count = documents.filter(d => d.type === dt.value).length;
                if (!count) return null;
                return (
                  <button key={dt.value} onClick={() => setDocFilter(dt.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap
                      ${docFilter === dt.value ? 'bg-[#0055FF] text-white' : 'bg-[#F4F4F5] text-[#71717A] hover:bg-[#E4E4E7]'}`}>
                    {dt.label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Document list */}
          <AnimatePresence>
            {filteredDocs.length === 0 ? (
              <p className="text-center text-[#71717A] py-6">Nessun documento</p>
            ) : (
              filteredDocs.map((doc, i) => {
                const docType = DOC_TYPES.find(dt => dt.value === doc.type) || DOC_TYPES[3];
                return (
                  <motion.div key={doc.doc_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex items-center gap-3 p-3 bg-white border border-[#E4E4E7] rounded-lg hover:shadow-sm transition-shadow">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: docType.color + '15' }}>
                      <docType.icon className="w-5 h-5" style={{ color: docType.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#09090B] text-sm truncate">{doc.title || doc.original_filename}</p>
                      <p className="text-xs text-[#71717A]">
                        {docType.label} · {doc.uploaded_by_name} · {new Date(doc.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => downloadDocument(doc)} data-testid={`download-doc-${doc.doc_id}`}>
                        <Download className="w-4 h-4 text-[#0055FF]" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteDocument(doc.doc_id)} data-testid={`delete-doc-${doc.doc_id}`}>
                        <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Work Orders Tab */}
      {activeTab === 'work_orders' && (
        <div className="space-y-3">
          {work_orders.length === 0 ? (
            <p className="text-center text-[#71717A] py-6">Nessun buono di lavoro</p>
          ) : (
            work_orders.map((wo, i) => (
              <motion.div key={wo.order_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="p-4 bg-white border border-[#E4E4E7] rounded-lg hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#09090B]">Buono #{wo.order_id.slice(-6).toUpperCase()}</p>
                    <p className="text-sm text-[#71717A]">{wo.date} · {wo.user_name} {wo.user_surname}</p>
                    {wo.description && (
                      <p className="text-sm text-[#52525B] mt-1 line-clamp-2">{wo.description}</p>
                    )}
                    <div className="flex gap-3 mt-1 text-xs text-[#71717A]">
                      <span><Users className="w-3 h-3 inline mr-0.5" />{wo.workers_count} operai</span>
                      <span>{wo.hours_worked}h</span>
                      {wo.signature_path && <span className="text-[#34C759]">✓ Firmato</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadWorkOrderPdf(wo.order_id)}
                    data-testid={`download-wo-${wo.order_id}`}>
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Media Tab */}
      {activeTab === 'media' && (
        <div className="space-y-3">
          {work_logs.length === 0 || work_logs.every(l => !l.media?.length) ? (
            <p className="text-center text-[#71717A] py-6">Nessun media</p>
          ) : (
            work_logs.filter(l => l.media?.length > 0).map(log => (
              <div key={log.log_id} className="space-y-2">
                <p className="text-sm font-semibold text-[#09090B]">
                  {log.date} · {log.user_name} {log.user_surname}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {log.media.map(m => {
                    const isVideo = (m.content_type || '').startsWith('video');
                    return (
                      <div key={m.media_id} className="aspect-square rounded-lg bg-[#F4F4F5] border border-[#E4E4E7] flex items-center justify-center overflow-hidden">
                        {isVideo ? (
                          <Video className="w-8 h-8 text-[#71717A]" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-[#71717A]" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ---- Main Clients Page ----
const ClientsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clients`, {
        params: search ? { search } : {}, withCredentials: true
      });
      setClients(res.data);
    } catch (e) { /* silenced */ }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Show client folder
  if (selectedClientId) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <ClientFolder clientId={selectedClientId} onBack={() => { setSelectedClientId(null); fetchClients(); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="clients-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
          <h1 className="text-lg font-bold text-[#09090B] flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-[#0055FF]" /> Clienti
          </h1>
          <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
            onClick={() => setShowForm(true)} data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* New client form */}
        {showForm && (
          <ClientForm onSave={() => { setShowForm(false); fetchClients(); }}
            onCancel={() => setShowForm(false)} />
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
          <Input className="pl-10" placeholder="Cerca clienti..." value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="client-search" />
        </div>

        {/* Client list */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0055FF]" /></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <FolderOpen className="w-12 h-12 text-[#E4E4E7] mx-auto mb-3" />
            <p className="text-[#71717A]">Nessun cliente trovato</p>
            <Button size="sm" className="mt-3 bg-[#0055FF] text-white" onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-1" /> Crea il primo cliente
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {clients.map((c, i) => (
              <motion.div key={c.client_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => setSelectedClientId(c.client_id)}
                className="flex items-center gap-3 p-4 bg-white border border-[#E4E4E7] rounded-lg hover:shadow-md hover:border-[#0055FF]/30 transition-all cursor-pointer group"
                data-testid={`client-card-${c.client_id}`}>
                <div className="w-11 h-11 rounded-full bg-[#0055FF]/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-[#0055FF]">{(c.name || '?')[0].toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#09090B] truncate">{c.name}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-[#71717A]">
                    {c.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" /> {c.phone}</span>}
                    {c.address && <span className="flex items-center gap-0.5 truncate"><MapPin className="w-3 h-3 shrink-0" /> {c.address}</span>}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#0055FF] transition-colors shrink-0" />
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientsPage;
